import React, { useMemo } from 'react'

import { DashboardPermissionGuard } from '@components'
import { Banner, Subsection, useModalContext } from '@fourthwall/components'
import {
  AddSubscriptionModal,
  SubscriptionStorageLabel,
  UpgradeSubscriptionBanner,
  useSubscription,
  useSubscriptionStorageLimits,
} from '@modules/Dashboard/modules/Subscriptions'

import * as S from './StorageLimits.styled'
import { StorageLimit } from './component/StorageLimit/StorageLimit'

export const StorageLimits = () => {
  const { open } = useModalContext()
  const {
    activeSubscription,
    canUpgradeSubscription,
    isLoading: isSubscriptionLoading,
  } = useSubscription()
  const { digitalProductsStorage, productDesignerStorage } = useSubscriptionStorageLimits()

  const isLoading = useMemo(
    () => !activeSubscription || isSubscriptionLoading,
    [activeSubscription, isSubscriptionLoading],
  )

  const shouldShowBanner = useMemo(() => {
    return (
      !isLoading && (digitalProductsStorage.isLimitReached || digitalProductsStorage.isNearLimit)
    )
  }, [isLoading, digitalProductsStorage])

  const openAddSubscriptionModal = () => {
    open(AddSubscriptionModal, {
      source: 'settings_plan',
    })
  }

  const renderBanner = () => {
    if (!shouldShowBanner) return null
    const isNearLimit = digitalProductsStorage.isNearLimit

    if (!canUpgradeSubscription) {
      return (
        <Banner appearance="warning">
          {isNearLimit ? (
            <>
              You’re close to reaching your{' '}
              <SubscriptionStorageLabel size={digitalProductsStorage.limit} /> storage limit
            </>
          ) : (
            <>
              You’ve reached your storage limit of{' '}
              <SubscriptionStorageLabel size={digitalProductsStorage.limit} />
            </>
          )}
          . Delete some files or email us at{' '}
          <a href="mailto:support@fourthwall.com">contact our support team</a> to request a higher
          storage limit.
        </Banner>
      )
    }

    if (!digitalProductsStorage.topSubscriptionPlanLimit) return null

    return (
      <UpgradeSubscriptionBanner source="settings_plan" onClick={openAddSubscriptionModal}>
        {isNearLimit ? (
          <>
            You’re close to reaching your{' '}
            <SubscriptionStorageLabel size={digitalProductsStorage.limit} /> storage limit
          </>
        ) : (
          <>
            You’ve reached your storage limit of{' '}
            <SubscriptionStorageLabel size={digitalProductsStorage.limit} />
          </>
        )}
        . Upgrade to get{' '}
        <SubscriptionStorageLabel size={digitalProductsStorage.topSubscriptionPlanLimit} /> of
        digital product file limit.
      </UpgradeSubscriptionBanner>
    )
  }

  if (!digitalProductsStorage.enabled && !productDesignerStorage.enabled) {
    return null
  }

  return (
    <DashboardPermissionGuard permission="settings.plan.storageLimits">
      <Subsection title="Storage limits">
        <S.Content>
          {digitalProductsStorage.enabled && (
            <StorageLimit
              title={`Digital product file ${digitalProductsStorage.isUnlimited ? 'usage' : 'limit'}`}
              currentSize={digitalProductsStorage.usage}
              maxSize={digitalProductsStorage.limit}
              isLoading={isLoading}
            />
          )}
          {productDesignerStorage.enabled && (
            <StorageLimit
              title={`Product designer file ${productDesignerStorage.isUnlimited ? 'usage' : 'limit'}`}
              currentSize={productDesignerStorage.usage}
              maxSize={productDesignerStorage.limit}
              isLoading={isLoading}
            />
          )}
          {renderBanner()}
        </S.Content>
      </Subsection>
    </DashboardPermissionGuard>
  )
}
